import {
  UpdateVaccinationActBodyVaccinationAct,
  VaccinationAct,
  VaccinationActApiInterface,
} from "@syadem/kairos-citizen-js";
import { ActionResult } from "../actionResult";
import { Service } from "./service";

interface Payload {
  vaccinationAct: VaccinationAct;
  updatedVaccinationAct: UpdateVaccinationActBodyVaccinationAct;
  healthRecordId: string;
}

function normalizeVaccinationAct(vaccinationAct: UpdateVaccinationActBodyVaccinationAct) {
  const normalizeValue = (value: unknown): unknown => {
    if (value === "" || value === undefined) return null;
    if (value instanceof Date) return value;
    if (Array.isArray(value)) return value.map(normalizeValue);

    if (value !== null && typeof value === "object") {
      return Object.fromEntries(
        Object.entries(value).map(([field, fieldValue]) => [field, normalizeValue(fieldValue)])
      );
    }

    return value;
  };

  return normalizeValue(vaccinationAct) as UpdateVaccinationActBodyVaccinationAct;
}

function valuesAreEqual(firstValue: unknown, secondValue: unknown): boolean {
  if (firstValue instanceof Date && secondValue instanceof Date) {
    return firstValue.getTime() === secondValue.getTime();
  }

  if (Array.isArray(firstValue) && Array.isArray(secondValue)) {
    return (
      firstValue.length === secondValue.length &&
      firstValue.every((value, index) => valuesAreEqual(value, secondValue[index]))
    );
  }

  if (
    firstValue !== null &&
    secondValue !== null &&
    typeof firstValue === "object" &&
    typeof secondValue === "object"
  ) {
    const firstEntries = Object.entries(firstValue);
    const secondEntries = Object.entries(secondValue);
    return (
      firstEntries.length === secondEntries.length &&
      firstEntries.every(([field, value]) =>
        valuesAreEqual(value, (secondValue as Record<string, unknown>)[field]),
      )
    );
  }

  return firstValue === secondValue;
}

function updatedFields(
  vaccinationAct: VaccinationAct,
  updatedVaccinationAct: UpdateVaccinationActBodyVaccinationAct,
): UpdateVaccinationActBodyVaccinationAct {
  const normalizedVaccinationAct = normalizeVaccinationAct(updatedVaccinationAct);

  return Object.fromEntries(
    Object.entries(normalizedVaccinationAct).filter(
      ([field, value]) => !valuesAreEqual(value, vaccinationAct[field as keyof VaccinationAct]),
    ),
  ) as UpdateVaccinationActBodyVaccinationAct;
}

export class UpdateVaccinationActService implements Service<Payload, VaccinationAct> {
  constructor(private readonly vaccinationActApi: VaccinationActApiInterface) {}

  async call({ vaccinationAct, updatedVaccinationAct, healthRecordId }: Payload): Promise<ActionResult<VaccinationAct>> {
    const vaccinationActUpdates = updatedFields(vaccinationAct, updatedVaccinationAct);

    if (Object.keys(vaccinationActUpdates).length === 0) {
      return {
        type: "success",
        value: vaccinationAct,
      };
    }

    const apiVaccinationAct = (
      await this.vaccinationActApi.updateVaccinationAct(
        healthRecordId,
        vaccinationAct.id,
        { vaccinationAct: vaccinationActUpdates }
      )
    ).vaccinationAct;

    return {
      type: "success",
      value: apiVaccinationAct,
    };
  }
}
